import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const PersonasContext = createContext(null);

function cacheKey(userId) { return userId ? `bf_personas_${userId}` : null; }

export function PersonasProvider({ children }) {
  const { user } = useAuth();
  const [personas, setPersonas] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [limits, setLimits] = useState(null);

  const persist = useCallback((next) => {
    if (!user) return;
    try { localStorage.setItem(cacheKey(user.id), JSON.stringify(next)); } catch { /* ignore */ }
  }, [user]);

  const refresh = useCallback(async (archived = includeArchived) => {
    try {
      const { data } = await api.get(`/personas${archived ? "?include_archived=true" : ""}`);
      setPersonas(data);
      setLoaded(true);
      persist(data);
      return data;
    } catch {
      return null;
    }
  }, [includeArchived, persist]);

  const refreshLimits = useCallback(async () => {
    try {
      const { data } = await api.get("/personas/_limits");
      setLimits(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!user) { setPersonas([]); setLoaded(false); setLimits(null); return; }
    const k = cacheKey(user.id);
    try {
      const raw = localStorage.getItem(k);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setPersonas(parsed);
      }
    } catch { /* ignore */ }
    refresh(false);
    refreshLimits();
  }, [user, refresh, refreshLimits]);

  const touch = useCallback(async (id) => {
    try {
      const { data } = await api.post(`/personas/${id}/touch`);
      setPersonas((arr) => {
        const next = arr.map((p) => p.id === id ? { ...p, ...data } : p);
        next.sort((a, b) => {
          if (!!b.pinned - !!a.pinned) return (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
          return (b.last_opened_at || "").localeCompare(a.last_opened_at || "");
        });
        persist(next);
        return next;
      });
    } catch { /* ignore */ }
  }, [persist]);

  const togglePin = useCallback(async (id) => {
    const { data } = await api.post(`/personas/${id}/pin`);
    setPersonas((arr) => {
      const next = arr.map((p) => p.id === id ? { ...p, ...data } : p);
      next.sort((a, b) => {
        if (!!b.pinned - !!a.pinned) return (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
        return (b.last_opened_at || "").localeCompare(a.last_opened_at || "");
      });
      persist(next);
      return next;
    });
    return data;
  }, [persist]);

  const toggleArchive = useCallback(async (id) => {
    const { data } = await api.post(`/personas/${id}/archive`);
    setPersonas((arr) => {
      let next = arr.map((p) => p.id === id ? { ...p, ...data } : p);
      if (!includeArchived && data.archived) next = next.filter((p) => p.id !== id);
      persist(next);
      return next;
    });
    return data;
  }, [includeArchived, persist]);

  const remove = useCallback(async (id) => {
    await api.delete(`/personas/${id}`);
    setPersonas((arr) => {
      const next = arr.filter((p) => p.id !== id);
      persist(next);
      return next;
    });
    await refreshLimits();
  }, [persist, refreshLimits]);

  const create = useCallback(async (payload) => {
    const { data } = await api.post("/personas", payload);
    setPersonas((arr) => {
      const next = [data, ...arr];
      persist(next);
      return next;
    });
    await refreshLimits();
    return data;
  }, [persist, refreshLimits]);

  const updateOne = useCallback((id, patch) => {
    setPersonas((arr) => {
      const next = arr.map((p) => p.id === id ? { ...p, ...patch } : p);
      persist(next);
      return next;
    });
  }, [persist]);

  const value = useMemo(() => ({
    personas,
    loaded,
    includeArchived,
    setIncludeArchived,
    limits,
    refresh,
    refreshLimits,
    touch,
    togglePin,
    toggleArchive,
    remove,
    create,
    updateOne,
  }), [personas, loaded, includeArchived, limits, refresh, refreshLimits, touch, togglePin, toggleArchive, remove, create, updateOne]);

  return <PersonasContext.Provider value={value}>{children}</PersonasContext.Provider>;
}

export function usePersonas() {
  const ctx = useContext(PersonasContext);
  if (!ctx) throw new Error("usePersonas must be used within PersonasProvider");
  return ctx;
}
